import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { openExternal } from '../../../utils/openExternal';
import { removeHttpAndSlash } from '../utils/removeHttpRegex';
import { presaveCreateMessages } from './presaveCreateMessages';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
import { trackEvent } from '../../analytics/sml/analytics';
/* eslint-enable css-modules/no-unused-class */

/** How long "Copied" shows after a copy. */
const COPIED_MS = 2000;

interface PresaveLinkBoxProps {
  /** The public address with its scheme. */
  shareUrl: string;
  /** The text a share sheet sends with the link. */
  shareTitle: string;
}

/** The live address with Copy, Share and Open (design presave-create b1, b2, c3). */
const PresaveLinkBoxComponent: React.FC<PresaveLinkBoxProps> = ({ shareUrl, shareTitle }) => {
  const intl = useIntl();
  const withLang = usePreviewPageLang();
  const [isCopied, setIsCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    if (!isCopied) {
      return undefined;
    }
    const timer = window.setTimeout(() => setIsCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [isCopied]);

  const onCopy = useCallback(() => {
    copy(shareUrl);
    setIsCopied(true);
    trackEvent('link_copied', { kind: 'presave' });
  }, [shareUrl]);
  const onShare = useCallback(() => {
    navigator.share({ title: shareTitle, url: shareUrl }).catch(console.error);
  }, [shareTitle, shareUrl]);
  const onOpen = useCallback(() => {
    openExternal(withLang(shareUrl)).catch(console.error);
  }, [shareUrl, withLang]);

  return (
    <div className={styles.linkBox} data-testid="presave-link-box">
      <span className={styles.liveDot} />
      <span className={styles.linkUrl}>{removeHttpAndSlash(shareUrl)}</span>
      <SMLButton
        size="sm"
        color={isCopied ? undefined : 'primary'}
        outline={isCopied}
        omitCap
        leftIcon={isCopied ? faCheck : faCopy}
        onClick={onCopy}
        testID="presave-link-copy"
      >
        {intl.formatMessage(isCopied ? presaveCreateMessages.copied : presaveCreateMessages.copy)}
      </SMLButton>
      {canShare && (
        <SMLButton size="sm" outline omitCap leftIcon={faArrowUpFromBracket} onClick={onShare}>
          {intl.formatMessage(presaveCreateMessages.share)}
        </SMLButton>
      )}
      <SMLButton size="sm" outline omitCap leftIcon={faArrowUpRightFromSquare} onClick={onOpen}>
        {intl.formatMessage(presaveCreateMessages.open)}
      </SMLButton>
    </div>
  );
};

export const PresaveLinkBox = TypedMemo(PresaveLinkBoxComponent);
